/**
 * ResponsiveScene — helper for scenes that need to re-layout on window
 * resize / orientation change under `Scale.RESIZE` mode.
 *
 * Usage:
 *   onResize(this, () => this._layout());
 *
 * `_layout()` should be idempotent: on the first call (from create) it
 * builds the scene; on subsequent calls (from resize events) it wipes
 * whatever was built and rebuilds at the new dimensions. The helper
 * handles listener installation, debouncing (resize events fire at up
 * to 60Hz while the user drags a window corner), and automatic
 * teardown on scene shutdown.
 *
 * Scenes that hold network state (Lobby) or match state (UserGame)
 * shouldn't blow themselves away on resize. They can still use this
 * helper with a layout function that repositions existing objects
 * instead of rebuilding them — or pass their own render-mode method.
 */
export function onResize(scene, layoutFn, opts = {}) {
  const debounceMs = opts.debounceMs == null ? 120 : opts.debounceMs;
  // Initial layout.
  try { layoutFn(); } catch (err) { if (typeof console !== 'undefined') console.error('[onResize initial]', err); }

  if (!scene.scale || typeof scene.scale.on !== 'function') return;

  let pendingTimer = null;
  const handler = () => {
    if (pendingTimer != null) {
      clearTimeout(pendingTimer);
    }
    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      try { layoutFn(); } catch (err) { if (typeof console !== 'undefined') console.error('[onResize layout]', err); }
    }, debounceMs);
  };

  scene.scale.on('resize', handler);
  scene.events.once('shutdown', () => {
    if (pendingTimer != null) clearTimeout(pendingTimer);
    scene.scale.off('resize', handler);
  });
  scene.events.once('destroy', () => {
    if (pendingTimer != null) clearTimeout(pendingTimer);
    scene.scale.off('resize', handler);
  });
}

/**
 * Returns an object with `wipe()` and `add(obj)` to track game objects so
 * a scene's layout function can blow away what it built last time and
 * reassemble fresh. Useful for the simple "rebuild-on-resize" pattern:
 *
 *   const slot = layoutSlot();
 *   onResize(this, () => {
 *     slot.wipe();
 *     slot.add(this.add.text(W/2, H/2, 'hi'));
 *     ...
 *   });
 */
export function layoutSlot() {
  const items = [];
  return {
    add(obj) {
      if (obj) items.push(obj);
      return obj;
    },
    wipe() {
      for (const o of items) {
        if (!o) continue;
        if (typeof o.destroy === 'function') {
          try { o.destroy(); } catch (_) { /* ignore */ }
        }
      }
      items.length = 0;
    },
    get size() { return items.length; }
  };
}
